import { Controller } from "@hotwired/stimulus"

// Cornerstone spike: shows and hides the sidebar in cs-page's navigation slot
// (shared/_navigation), sliding it with Cornerstone's <cs-animation> and its built-in
// slideInLeft / slideOutLeft. Those move the sidebar but not the page beside it, so the page
// takes the sidebar's space back only once the slide out ends (the frame is then collapsed:
// display: none on desktop, so out of the tab order and the accessibility tree too), and
// gives it up at once on show. Someone who asks for reduced motion gets no slide, as
// Cornerstone's animation docs ask.
//
// Collapsed is a data-collapsed attribute on the frame, not `hidden`: it only applies on
// desktop (cornerstone/page.css). In the mobile drawer the sidebar always shows, and the
// app's base styles make `hidden` win over any rule that tries to show it there.
//
// The timing is the design system's: --cs-transition-slow, read at runtime because
// cs-animation's `duration` takes milliseconds, not a token.
//
// Shown or hidden is a cookie the server reads (SidebarHelper#sidebar_open?, and the ETag in
// ApplicationController), so the server renders the frame and the Show button the way the
// person left them, and a morph refresh agrees with what is on screen. After a morph the
// controller takes its state from that HTML (refresh). On connect the cookie wins over a
// Turbo snapshot taken before the last change.
//
// Markup: on <cs-page>, data-controller="app-sidebar" and
// data-action="turbo:morph@document->app-sidebar#refresh". Targets: the frame, and the
// <cs-animation> inside it, whose one child is the sidebar column. Buttons: the sidebar's own
// (click->app-sidebar#hide, the "hideButton" target) and the breadcrumb row's
// (click->app-sidebar#show, the "showButton" target), which is only there while the sidebar
// is hidden. Focus moves to whichever button remains, so it is never left in a hidden one.
const COOKIE = "fd_sidebar_open"
const ONE_YEAR = 60 * 60 * 24 * 365

export default class extends Controller {
  static targets = ["frame", "animation", "hideButton", "showButton"]

  connect() {
    // `open` is the intent; the frame lags it by a slide out, so a click mid-slide reverses it.
    const saved = this.read()
    this.open = saved === null ? !this.collapsed : saved
    this.collapse(!this.open)
    this.sync()
  }

  // A morph refresh redrew the frame and the Show button from the server's HTML.
  refresh() {
    this.open = !this.collapsed
    this.sync()
  }

  show() {
    if (this.open) return
    this.open = true
    this.collapse(false)
    if (!this.reducedMotion) this.play("slideInLeft", "easeOutCubic")
    this.write(true)
    this.sync()
    // A frame later: the frame has only just stopped being collapsed.
    requestAnimationFrame(() => { if (this.hasHideButtonTarget) this.hideButtonTarget.focus() })
  }

  hide() {
    if (!this.open) return
    this.open = false
    // Collapse, then drop the slide's held end position, so nothing later can show the
    // sidebar still slid out of view.
    const finish = () => { this.collapse(true); this.animationTarget.cancel() }
    if (this.reducedMotion) finish()
    else this.play("slideOutLeft", "easeInCubic", finish)
    this.write(false)
    this.sync()
    this.showButtonTargets[0]?.focus()
  }

  get collapsed() {
    return this.frameTarget.hasAttribute("data-collapsed")
  }

  collapse(collapsed) {
    this.frameTarget.toggleAttribute("data-collapsed", collapsed)
  }

  get reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  // A click can land mid-slide (hide, then show before it ends). cs-animation starts on a
  // CHANGE to `play`, so setting it true while it is already true does nothing: stop it,
  // let the element update, then start the new slide. A replaced slide's finish handler is
  // dropped, so a cut-short hide never collapses the frame afterwards.
  async play(name, easing, onFinish) {
    const animation = this.animationTarget
    if (this.onFinish) animation.removeEventListener("cs-finish", this.onFinish)
    this.onFinish = null
    animation.play = false
    await animation.updateComplete
    animation.name = name
    animation.easing = easing
    animation.duration = this.duration()
    animation.iterations = 1
    // Hold the slid-out position until the frame collapses, so it does not flash back.
    animation.fill = onFinish ? "forwards" : "none"
    if (onFinish) {
      this.onFinish = () => { this.onFinish = null; onFinish() }
      animation.addEventListener("cs-finish", this.onFinish, { once: true })
    }
    animation.play = true
  }

  // The breadcrumb row's show button is only there while the sidebar is hidden.
  sync() {
    this.showButtonTargets.forEach((button) => { button.hidden = this.open })
  }

  duration() {
    const value = getComputedStyle(document.documentElement).getPropertyValue("--cs-transition-slow").trim()
    const ms = value.endsWith("ms") ? parseFloat(value) : parseFloat(value) * 1000
    return Number.isFinite(ms) ? ms : 300
  }

  read() {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([01])`))
    return match ? match[1] === "1" : null
  }

  write(open) {
    document.cookie = `${COOKIE}=${open ? "1" : "0"}; path=/; max-age=${ONE_YEAR}; SameSite=Lax`
  }
}
